/**
 * State-specific HIPAA consent ages for general medical care.
 *
 * Most states follow the standard age of 18. These are the exceptions:
 * - Alabama: 14
 * - Oregon: 15
 * - Rhode Island: 16
 * - South Carolina: 16
 * - Nebraska: 19
 *
 * NOTE: The product rule uses 18 as the universal dividing line.
 * State-specific ages are stored for reference and future use.
 * The attestation checkbox is the actual legal shield.
 */
export const STATE_CONSENT_AGES: Record<string, number> = {
  AL: 14,
  OR: 15,
  RI: 16,
  SC: 16,
  NE: 19,
};

/** All US states + DC */
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
] as const;

/**
 * Get the HIPAA consent age for a given US state.
 * Returns 18 for most states (the standard).
 */
export function getConsentAge(stateCode: string): number {
  return STATE_CONSENT_AGES[stateCode.toUpperCase()] ?? 18;
}

/**
 * Calculate age from a date of birth string (YYYY-MM-DD).
 */
export function calculateAge(dateOfBirth: string): number {
  // Parse date parts explicitly to avoid UTC-midnight offset.
  // new Date("YYYY-MM-DD") is treated as UTC, which shifts the date back
  // 1 day for US timezones — causing age to be miscalculated near birthdays
  // and potentially misidentifying minors in HIPAA consent flows.
  const [year, month, day] = dateOfBirth.split('-').map(Number);
  const dob = new Date(year, month - 1, day);
  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

/**
 * Product rule: Is the patient under 18?
 * Uses 18 as the universal dividing line (not state-specific).
 * The attestation checkbox handles the legal nuance.
 */
export function isMinorForHipaa(dateOfBirth: string): boolean {
  return calculateAge(dateOfBirth) < 18;
}
